/**
 * PATH: packages/ui/src/components/email/row/rowGrammar.ts
 * FILE_ID: UI-EMAIL-ROW-GRAMMAR-001
 * PURPOSE: The single source of truth for the inbox row's fixed-column
 *          LAYOUT grammar — the class strings every EmailRow zone consumes
 *          so all rows (allocated or not) share ONE uniform shape.
 * WHY:     The pre-refactor row mixed a heavy rounded card FRAME (allocated)
 *          with a flat list row (unallocated) and let ~12 chips share one
 *          horizontally-scrolling strip — uneven heights, "tags all over",
 *          "ux kinda broken" (FR-0638). A fixed grammar keeps every row the
 *          same height so the eye scans a column, not a ragged stack.
 * FLOW:    (zone) -> class string -> EmailRow / row sub-components.
 * RELATED:
 *   - row/resolveRowAccent.ts: the pure state→accent resolver this pairs with.
 *   - EmailRow.tsx: the thin composer that assembles the zones.
 */

/**
 * The META zone (category pill · Board>Lane chip · attachments · tags).
 * SINGLE-LINE at EVERY breakpoint — never wrap. A wrapping strip made busy
 * rows tower over quiet ones (BUG-2026-06-16 family) and produced the
 * "glitchy" uneven-height scroll. No-wrap + horizontal scroll + hidden
 * scrollbar keeps uniform row height everywhere. The fixed left-to-right
 * grammar means the highest-signal chips lead and only low-signal glances
 * scroll off on the rare overflow.
 *
 * Kept named ``CHIP_STRIP_LAYOUT`` for continuity with the source-pin guard
 * (EmailRow.mobileChipStrip.pin.test) that forbids re-introducing wrap.
 */
export const CHIP_STRIP_LAYOUT =
  'flex items-center gap-1.5 flex-nowrap overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'

/**
 * The UNIFORM chip grammar — every classification / attribution chip in the
 * strip (category · work · mailbox · suggestion · tags · calendar) shares ONE
 * height, radius, padding and text size, so the run reads as a calm even line
 * instead of the ragged mix of 8–10px pills the operator called "completely
 * fucked" / "tags all over" (FR-0638; 2026-06-22 screenshot). Callers add only
 * their own COLOUR (bg/border/text) and any shrink-0 / max-w cap.
 *
 * - ``text-[10px]`` is the team's established "compact but legible" floor (the
 *   tag pills + the kanban-host lane chip already sit here; sub-10px was the
 *   "hard to read" complaint). Holding the WHOLE strip at 10px keeps the work
 *   chip + its adjacent lane handle (AllocationChip, ``text-[10px]``, shared
 *   across many surfaces so left untouched) perfectly matched.
 * - ``py-0.5`` (not a fixed h-*) matches the lane chip's height; ``rounded-full``
 *   matches the lane chip + every existing pill, so the strip stays ONE shape
 *   and ONE size instead of the ragged 8–10px mix.
 */
export const CHIP_BASE =
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium'

/** Root row: uniform flat row with a thin left colour-accent rail. NO card
 *  frame — the accent carries the "this is work on board X" glance signal at
 *  a fraction of the frame's visual weight, so every row is the same height. */
export const ROW_ROOT =
  'group relative cursor-pointer transition-colors duration-100 border-b border-border/50'

/** The 3px left accent rail width. Allocated rows colour it (board/category
 *  hue); unallocated rows keep it transparent so the column edge stays clean. */
export const ROW_ACCENT_WIDTH = '3px'

/** The padded row container — a VERTICAL stack of two zones: the identity
 *  HEADER row (checkbox · avatar · sender/subject/preview) and, BELOW it, the
 *  full-width FOOTER (classification chip run + disposition bar). The footer
 *  is a sibling of the header (not nested in the identity text column) so it
 *  starts flush at the container's left padding — i.e. at the CHECKBOX's left
 *  edge — instead of indenting under the avatar (operator 2026-06-23, with
 *  screenshot: the category chip + star "should start where the checkbox is
 *  at the leftmost side"). */
export const ROW_BODY = 'relative px-3 py-2.5'

/** P55 — denser body padding for compact inbox density. */
export const ROW_BODY_COMPACT = 'relative px-3 py-1.5'

/** The identity HEADER row inside ROW_BODY: checkbox · avatar · identity(flex).
 *  ``items-start`` so a multi-line identity tops-aligns with the checkbox +
 *  avatar. The chip run + action bar live OUTSIDE this row (see ROW_BODY) so
 *  they span the full width and align to the checkbox, not the avatar. */
export const ROW_HEADER = 'flex items-start gap-2.5'

/** The demoted secondary cluster (mailbox / relation / company). Visible but
 *  de-emphasised so it stops competing with the subject; leans to full on row
 *  hover. NOT opacity-0 (that made shared-mailbox attribution undiscoverable). */
export const ROW_SECONDARY = 'inline-flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity'

/** Resolve the inline style for the left accent rail. Transparent when the
 *  row isn't allocated so unallocated rows read as a clean flat list. */
export function rowAccentStyle(
  isAllocated: boolean,
  accentColor: string,
  isSelected: boolean,
): React.CSSProperties {
  if (!isAllocated && !isSelected) {
    return { borderLeftWidth: ROW_ACCENT_WIDTH, borderLeftColor: 'transparent' }
  }
  const color = isSelected && !isAllocated ? 'var(--primary, #6366f1)' : accentColor
  return {
    borderLeftWidth: ROW_ACCENT_WIDTH,
    borderLeftColor: color,
  }
}
